import { useTranslation } from 'react-i18next'
import { observe, useObserver, viewportWidth } from 'react-ui-observer'
import get from 'lodash/get'
import styled, { css } from 'styled-components'

import { breakpoints } from 'bl-common/src/constants/breakpoints'
import { Link } from 'bl-common/src/elements/Link'
import { Type } from 'bl-common/src/elements/Typography/Typography'
import { Carousel } from 'bl-common/src/units/Carousel/Carousel'
import { Section } from 'bl-common/src/units/Section/Section'
import { media } from 'bl-common/src/utils/media'

import { LatestStory } from './LatestStory'
import { ReadMore } from './ReadMore'
import { StoryCard } from './StoryCard'

const LatestStoriesSection = styled(Section)`
  overflow-x: hidden;
`

const Container = styled.div`
  ${media.md(css`
    display: flex;
  `)}
`

const OtherStoriesContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 40px;
  margin-left: 40px;
  flex: 1;
`

const HeadlineWrap = styled.div`
  margin-bottom: 80px;
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  ${media.md(css``)}
`

const RenderLatestStory = ({ story }) => {
  const { t } = useTranslation()
  return story ? (
    <LatestStory
      title={story.fields.title}
      topic={story.fields.topic}
      date={story.fields.publishDate}
      image={story.fields.heroImage.fields.image}
      link={t('storyPathWithSlug', { slug: story.fields.slug })}
    />
  ) : null
}

const StoryCarousel = ({ stories }) => {
  return stories ? (
    <Carousel renderControls={false} pageMargin={2}>
      {stories.slice(0, 4).map(story => (
        <StoryCard
          key={story.fields.title}
          imageRatio={0.8}
          title={story.fields.title}
          topic={story.fields.topic}
          date={story.fields.publishDate}
          image={story.fields.heroImage.fields.image}
          link={story.fields.slug}
        />
      ))}
    </Carousel>
  ) : null
}

const RenderOtherStories = ({ isMobile, stories }) =>
  isMobile ? (
    <StoryCarousel stories={stories} />
  ) : (
    <OtherStoriesContainer>
      {stories.slice(0, 4).map(story => (
        <StoryCard
          key={story.sys.id}
          imageRatio={1.3}
          title={story.fields.title}
          topic={story.fields.topic}
          date={story.fields.publishDate}
          image={story.fields.heroImage.fields.image}
          link={story.fields.slug}
        />
      ))}
    </OtherStoriesContainer>
  )

export const LatestStories = ({
  stories,
  top = {},
  bottom = {},
  featuredStory = undefined,
  link = undefined,
}) => {
  const { t } = useTranslation()
  const [firstStory, secondStory, ...rest] = stories
  const shouldShuffleLatestStory =
    get(featuredStory, 'sys.id', 'featured') ===
    get(firstStory, 'sys.id', 'first')

  // TODO: Switch to useBreakpoints
  const isMobile = useObserver(
    observe(viewportWidth(), width => width < breakpoints.md)
  )

  return (
    <LatestStoriesSection top={top} bottom={bottom}>
      <HeadlineWrap>
        <Type preset="headlineMedium" as="h2" bottom={{ xs: 2.5 }}>
          {t('latestStories')}
        </Type>
        {link && (
          <Link to="/stories/all">
            <ReadMore />
          </Link>
        )}
      </HeadlineWrap>
      <Container>
        <RenderLatestStory
          story={shouldShuffleLatestStory ? secondStory : firstStory}
        />
        <RenderOtherStories
          isMobile={isMobile}
          stories={
            shouldShuffleLatestStory ? [firstStory, ...rest] : stories.slice(1)
          }
        />
      </Container>
    </LatestStoriesSection>
  )
}
